/**
 * Utility centralizzata per la fruizione degli asset media.
 *
 * Offre helper per risolvere URL di preview/inline vs download,
 * e per avviare il download forzato lato client (fallback fetch+blob).
 */

/**
 * Risolve l'URL appropriato per un asset in base alla modalità richiesta.
 *
 * @param {Object|string} asset – Oggetto con `url`, `preview_url`, `download_url`
 *                                oppure stringa URL semplice (backward compat)
 * @param {'preview'|'download'} mode
 * @returns {string}
 */
export function resolveAssetUrl(asset, mode = 'preview') {
    if (typeof asset === 'string') return asset;
    if (!asset) return '';

    if (mode === 'download') {
        return asset.download_url || asset.url || '';
    }
    return getPreviewUrl(asset);
}

const parseAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return null;

    try {
        return new URL(url, globalThis.location?.href || 'http://localhost');
    } catch {
        return null;
    }
};

export function isForcedDownloadUrl(url) {
    const parsedUrl = parseAssetUrl(url);
    if (!parsedUrl) return false;

    return Array.from(parsedUrl.searchParams.keys())
        .some((name) => name.toLowerCase() === 'response-content-disposition');
}

const isCrossOriginUrl = (url) => {
    const parsedUrl = parseAssetUrl(url);
    const currentOrigin = globalThis.location?.origin;

    return Boolean(parsedUrl && currentOrigin && parsedUrl.origin !== currentOrigin);
};

const triggerDirectDownload = (url, filename) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

/**
 * Avvia il download forzato di un file.
 *
 * Strategia:
 *   1. Se l'URL è un download_url con Content-Disposition: attachment,
 *      usa un link diretto (zero overhead).
 *   2. Fallback: fetch + blob + <a download> per garantire il download
 *      anche quando il server non imposta Content-Disposition.
 *
 * @param {string} url       URL del file (preferibilmente download_url)
 * @param {string} [filename] Nome file suggerito per il download
 */
export async function downloadAsset(url, filename) {
    if (!url) return;

    const resolvedFilename = filename
        || decodeURIComponent(url.split('/').pop()?.split('?')[0] || 'file');

    // Fetching a signed CDN URL requires CORS and makes the service worker
    // handle a download as an API request. Direct navigation honors the
    // server-side Content-Disposition without either requirement.
    if (isCrossOriginUrl(url) || isForcedDownloadUrl(url)) {
        triggerDirectDownload(url, resolvedFilename);
        return;
    }

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = resolvedFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    } catch {
        // Fallback: apri in nuova tab (il Content-Disposition del server gestirà il download)
        window.open(url, '_blank', 'noopener');
    }
}

/**
 * Costruisce l'URL per il download a partire da un oggetto downloadable
 * come esposto dal backend (Downloadable VO serializzato).
 *
 * @param {Object} downloadable – { filename, url, download_url, type }
 * @returns {string} URL per il download
 */
export function getDownloadUrl(downloadable) {
    return downloadable?.download_url || downloadable?.url || '';
}

/**
 * Costruisce l'URL per la preview a partire da un oggetto downloadable.
 *
 * @param {Object} downloadable – { filename, url, download_url, type }
 * @returns {string} URL per la visualizzazione inline
 */
export function getPreviewUrl(downloadable) {
    const candidates = [downloadable?.preview_url, downloadable?.url].filter(Boolean);

    return candidates.find((url) => !isForcedDownloadUrl(url)) || '';
}
